import { useCallback } from 'react'
import { type TrackingEventMetadata } from '../types'
import { useDomainIntl } from '../utils/intl'

interface Money {
  currencyCode: string
  nanos: string | number
  units: string | number
}

/**
 * Custom hook for formatting various values with error handling and tracking.
 *
 * @param trackEvent - Optional callback function for tracking formatting errors
 * @returns Object containing formatting functions
 *
 * @example
 * ```tsx
 * const { formatCurrency } = useFormattedValue((event, metadata) => {
 *   console.log('Formatting error:', event, metadata)
 * })
 *
 * const formatted = formatCurrency({
 *   currencyCode: 'USD',
 *   units: '15',
 *   nanos: '2500'
 * })
 * // Returns: "USD 15.25"
 * ```
 */
export const useFormattedValue = (
  trackEvent?: (event: string, metadata: TrackingEventMetadata) => void
) => {
  const { formatNumber } = useDomainIntl()

  /**
   * Formats a Money object into a currency string using Intl.NumberFormat.
   *
   * @param input - Money object containing currency information
   * @param input.currencyCode - ISO currency code (e.g., 'USD', 'EUR')
   * @param input.units - Whole currency units as string or number
   * @param input.nanos - Fractional currency units in nanoseconds (first 2 digits used for cents)
   * @param options - Additional Intl.NumberFormat options (excluding 'style' and 'currency')
   *
   * @returns Formatted currency string or '—' if formatting fails
   *
   * @example
   * ```tsx
   * // Basic usage
   * formatCurrency({
   *   currencyCode: 'USD',
   *   units: '10',
   *   nanos: '5000'
   * })
   * // Returns: "USD 10.50"
   *
   * // Invalid input returns fallback
   * formatCurrency(null)
   * // Returns: "—"
   * ```
   *
   * @remarks
   * - Units can be string or number but must be convertible to a valid number
   * - Nanos represents fractional currency units; only first 2 digits are used for cents
   * - If nanos is missing, defaults to 0
   * - Errors are tracked via the optional trackEvent callback
   * - Returns em-dash ('—') fallback character for any formatting errors
   */
  const formatCurrency = useCallback(
    (
      input?: Partial<Money>,
      options?: Exclude<Parameters<typeof formatNumber>[1], 'style' | 'currency'>
    ): string => {
      try {
        if (!input || typeof input !== 'object') {
          throw new Error('Invalid money object')
        }

        const currencyCode = input.currencyCode
        if (!currencyCode || typeof currencyCode !== 'string' || currencyCode.length !== 3) {
          throw new Error('Invalid or missing currency code')
        }

        const units = Number(input.units)
        if (input.units === '' || isNaN(units)) {
          throw new Error('Invalid units value in money object')
        }

        const nanosFromInput = Number(input.nanos || 0)
        const nanosString = nanosFromInput.toString().slice(0, 2).padStart(2, '0')
        const nanos = parseInt(nanosString, 10)

        if (isNaN(nanos)) {
          throw new Error('Invalid nanos value in money object')
        }

        const amount = units + nanos / 100

        return formatNumber(amount, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'code',
          ...options,
        })
      } catch (error) {
        if (trackEvent) {
          trackEvent('currency_formatting_error', {
            input: JSON.stringify(input),
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }

        return '—'
      }
    },
    [formatNumber, trackEvent]
  )

  return {
    formatCurrency,
  }
}
